import { isRejectedWithValue } from "@reduxjs/toolkit";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { showNotification } from "../actions/notification";

/**
 * A page under /capworks fires several queries at once — the dashboard layout
 * loads the profile on every route, on top of whatever the page itself needs.
 * When the session is no longer valid they all reject together, and without
 * this flag each rejection would assign window.location.href independently,
 * each navigation cancelling the last. That burst is what users see as the
 * page "flashing" or refusing to stop refreshing.
 *
 * One redirect per page load is enough: the first one is already taking the
 * browser somewhere, so every later rejection has nothing left to do.
 */
let redirecting = false;

/**
 * Sends the browser to `url` once. Later calls during the same page load are
 * dropped rather than queued.
 */
const redirectOnce = url => {
  if (redirecting) return;
  redirecting = true;

  // Let the rejected action finish reducing before navigating.
  setTimeout(() => {
    window.location.href = url;
  }, 0);
};

/**
 * True when we have just come from the Capworks login page, which means
 * signing in did not resolve the failure. Bouncing back to login would start
 * the loop again, so the user is shown the expired-session screen instead and
 * asked to sign in deliberately.
 */
const arrivedFromLogin = () => {
  try {
    return document.referrer ? new URL(document.referrer).pathname.startsWith("/capworks/login") : false;
  } catch {
    return false;
  }
};

export const requestErrorMiddleware = api => next => action => {
  if (isRejectedWithValue(action)) {
    const status = action.payload?.status;

    // Detect if we are on a capworks route
    const isCapworksRoute = window.location.pathname.startsWith("/capworks");

    // Handle 401 Unauthorized - the session is gone, so sign-in can fix it
    if (status === 401) {
      const errorMessage = encodeURIComponent(
        "Your session has expired or you are not authenticated. Please log in again.",
      );

      // eslint-disable-next-line no-console
      console.log("[RTK Error Middleware] 401 Unauthorized - redirecting to forbidden page");

      if (isCapworksRoute) {
        /*
         * Returning to login after login already sent us here means the stale
         * session token survived the sign-in, so another round trip would just
         * repeat. Stop and let the user start a fresh sign-in themselves.
         */
        if (arrivedFromLogin()) {
          redirectOnce("/capworks/session-expired");
        } else {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          redirectOnce(`/capworks/login?returnUrl=${returnUrl}`);
        }
      } else {
        redirectOnce(`/forbidden?error=${errorMessage}&status=401`);
      }

      return next(action);
    }

    /*
     * Handle 403 Forbidden.
     *
     * A 403 means the request was authenticated and refused — this account is
     * not allowed the resource. Signing in again cannot change that, so on a
     * Capworks route sending the user to login produced a loop with no exit:
     * they sign in, come back, are refused again, and are sent to login again.
     * Show them why instead.
     */
    if (status === 403) {
      const errorMessage = encodeURIComponent("You do not have permission to access this resource.");

      // eslint-disable-next-line no-console
      console.log("[RTK Error Middleware] 403 Forbidden - redirecting to forbidden page");

      redirectOnce(`/forbidden?error=${errorMessage}&status=403`);

      return next(action);
    }

    // For other errors, show notification (unless endpoint opted out)
    if (action.payload?.data && !action.meta?.baseQueryMeta?.suppressNotification)
      api.dispatch(
        showNotification({
          ...DEFAULT_NOTIFICATION_PAYLOAD,
          message: {
            title: "Error",
            description: action.payload.data.userDescription,
            options: {
              variant: NOTIFICATION_VARIANTS.ERROR,
            },
          },
        }),
      );
  }

  return next(action);
};
